import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Users, Clock, Ticket, HandHelping, Briefcase, ShieldCheck, Table2, BarChart3, Hammer, Package, Tags, IdCard, Trash2, DollarSign, Activity, BookHeart,
} from 'lucide-react';

const ITEMS = [
  {
    to: '/admin/reports',
    label: 'Reports & Insights',
    icon: BarChart3,
    testid: 'sidebar-link-reports',
  },
  {
    to: '/admin/memorials',
    label: 'Memorials',
    icon: BookHeart,
    testid: 'sidebar-link-memorials',
  },
  {
    to: '/admin/billing-health',
    label: 'Billing Health',
    icon: Activity,
    testid: 'sidebar-link-billing-health',
  },
  {
    to: '/admin/commissions',
    label: 'Commission Payouts',
    icon: DollarSign,
    testid: 'sidebar-link-commissions',
  },
  {
    to: '/admin/families',
    label: 'Family Accounts',
    icon: Table2,
    testid: 'sidebar-link-families',
  },
  {
    to: '/admin/partners/applications',
    label: 'Partner Applications',
    icon: Users,
    testid: 'sidebar-link-partner-applications',
  },
  {
    to: '/admin/partners/overrides',
    label: 'Override Contracts',
    icon: Clock,
    testid: 'sidebar-link-override-contracts',
  },
  {
    to: '/admin/promo-codes',
    label: 'Promo Codes',
    icon: Ticket,
    testid: 'sidebar-link-promo-codes',
  },
  {
    to: '/admin/assistance',
    label: 'Assistance Requests',
    icon: HandHelping,
    testid: 'sidebar-link-assistance-requests',
  },
  {
    to: '/admin/engraving-quotes',
    label: 'Engraving Quotes',
    icon: Hammer,
    testid: 'sidebar-link-engraving-quotes',
  },
  {
    to: '/admin/plaque-orders',
    label: 'Plaque Orders',
    icon: Package,
    testid: 'sidebar-link-plaque-orders',
  },
  {
    to: '/admin/products',
    label: 'Products',
    icon: Tags,
    testid: 'sidebar-link-products',
  },
  {
    to: '/admin/accounts',
    label: 'Accounts',
    icon: IdCard,
    testid: 'sidebar-link-accounts',
  },
  {
    to: '/admin/professional/applications',
    label: 'Professional (legacy)',
    icon: Briefcase,
    testid: 'sidebar-link-professional-applications',
  },
  {
    to: '/admin/live-cleanup',
    label: 'Live Cleanup',
    icon: Trash2,
    testid: 'sidebar-link-live-cleanup',
  },
];

export function AdminSidebar() {
  const { user } = useAuth();
  if (!user || !(user.role === 'admin' || user.role === 'platform_admin')) {
    return null;
  }
  return (
    <aside
      data-testid="admin-sidebar"
      className="hidden lg:block sticky top-[88px] self-start w-60 shrink-0"
      style={{ maxHeight: 'calc(100vh - 88px - 24px)' }}
    >
      <div className="bg-[#FFFBF0] rounded-xl border border-[hsl(var(--primary)/0.12)] p-3 shadow-[0_10px_30px_-20px_rgba(26,54,38,0.25)]">
        <div className="flex items-center gap-2 px-3 pt-2 pb-3 border-b border-[hsl(var(--primary)/0.08)] mb-2">
          <ShieldCheck size={14} className="text-[hsl(var(--accent))]" />
          <span className="text-[10px] tracking-[0.24em] uppercase text-[hsl(var(--primary)/0.65)] font-medium">
            Platform admin
          </span>
        </div>
        <nav className="flex flex-col gap-0.5">
          {ITEMS.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              end={false}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] transition-colors ${
                  isActive
                    ? 'bg-[hsl(var(--primary))] text-[hsl(var(--accent))] font-medium'
                    : 'text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.06)]'
                }`
              }
            >
              <Icon size={15} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </aside>
  );
}

/**
 * Wraps an admin page's main content with the sticky sidebar. The page
 * is expected to render its own `<Navbar />` above this layout.
 */
export function AdminLayout({ children }) {
  return (
    <main className="ml-container-wide py-10 lg:py-12">
      <div className="lg:flex gap-8 items-start">
        <AdminSidebar />
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </main>
  );
}
